// MA-495 — buildJourneyView v2: the pure, fold-derived view-model for the
// kid-facing "Journey" tab's full scrollable path + delight rebuild.
//
// Spec: docs/build/specs/MA-343v2-journey-delight-design.md (v1's spec,
// docs/build/specs/MA-343-journey-tab-design.md, is superseded on every
// point named below — ADR-100).
//
// THE CONTRACT:
//   - Stones = each chapter's `milestoneSkills` (big) AND `supportSkills`
//     (pebbles) — D3. 54 rendered stones today (10 + 44). Chapterless
//     microSkills still never render (class-based rule, unchanged from v1).
//   - The builder does NOT compute fold status itself. It is handed a
//     `statusById` map (the SAME `computeStrandStatuses` output the parent
//     tree renders) and only reshapes it — parity by construction, the
//     MA-93 class, unchanged from v1.
//   - Full path: every chapter renders in the returned view (the v1
//     chapter-scoped bound — before/current/teaser/absent — is retired,
//     ADR-100). `isMissionCurrent` marks the one chapter D4's window is
//     anchored to (the mission walk's chapter, or its fallback, unless MA-832
//     re-anchored it onto an earlier frontier chapter — see
//     `anchorWindowOnFrontier`), for the `journey-current-chapter` testid and
//     D4's chapter cap. MA-834: a placed child's age-covered mission skill in
//     the mission chapter is the served stone when the window holds no
//     startable frontier (see buildJourneyView).
//   - Chapter header state ("celebration"/"neutral") is the D1 WIDENED
//     rule: every rendered stone (milestones AND supports) must be in
//     {mastered, secure, age_covered}, at least one mastered/secure, and no
//     frontier (practicing/next) stone in the chapter.
//   - `tapEligibility` (D4) is computed and emitted per stone but NOT wired
//     to any handler this PR (PR-A). Lanes are GLOBAL per strand (spanning
//     all 4 chapters, fold-sorted), window [F-3,F+3] additionally capped to
//     {mission-current chapter, current+1} — this is what excludes the
//     fold-order teleports (ms.math.03 / ms.read.21) even when their raw
//     lane INDEX would fall inside the window.
//   - No age/grade/band word, and no raw fold-status token, is ever
//     emitted in a kid-audible/aria string (D4). KID_STATUS_LABEL is the
//     one allowed translation from Status -> kid copy.

import microSkills from "@/curriculum/data/manifests/microSkills.json" with { type: "json" };
import { ageBandRank, type Status } from "./skillTreeStatus";
import { foundationRedirectSkillId, skillCurriculumOrdinal } from "./placementFloor";
import { servedItemCountForSkill } from "./useEngineQuestions";
import { audienceForSkill } from "./skillMeta";
import { KID_LABEL_MAP } from "./kidLabelMap";

interface MicroSkillRow {
  id: string;
  label: string;
  ageBand: string;
  strand: "math" | "reading" | "writing";
  prerequisites?: string[];
}

const SKILLS = microSkills as MicroSkillRow[];
const BY_ID: Record<string, MicroSkillRow> = Object.fromEntries(
  SKILLS.map((s) => [s.id, s]),
);
const INDEX_BY_ID: Record<string, number> = Object.fromEntries(
  SKILLS.map((s, i) => [s.id, i]),
);

/** Same ordering key `statusForSkillInStrand` folds over: ageBandRank
 *  first, then the skill's manifest-declaration index as the stable
 *  tiebreak — identical to the tree's own strand fold order. This is the
 *  SAME key used for both a chapter's own lane order AND the GLOBAL,
 *  cross-chapter lane D4's window math runs over — a milestone or support
 *  skill whose ageBand sits earlier/later than its chapter siblings (the
 *  "fold-order inversion" fixtures, e.g. ms.math.03 / ms.read.21) is
 *  exactly what the chapter cap below exists to contain. */
function orderKey(id: string): [number, number] {
  const row = BY_ID[id];
  return [row ? ageBandRank(row.ageBand) : Number.MAX_SAFE_INTEGER, INDEX_BY_ID[id] ?? Number.MAX_SAFE_INTEGER];
}

function byStrandOrder(a: string, b: string): number {
  const [ar, ai] = orderKey(a);
  const [br, bi] = orderKey(b);
  return ar !== br ? ar - br : ai - bi;
}

// KID_LABEL_MAP (the D3 kid-register labels) lives in kidLabelMap.ts
// (MA-874 D-1101-23) and is re-exported here unchanged.
export { KID_LABEL_MAP };

function kidLabelFor(skillId: string): string {
  const label = KID_LABEL_MAP[skillId];
  if (label === undefined) {
    // D3: "No fallback to manifest labels for rendered stones — an
    // unmapped rendered id FAILS the bite." A future support→milestone
    // (or brand-new chapter skill) promotion that forgets to extend
    // KID_LABEL_MAP must throw here, loud, rather than leak an adult-
    // register manifest label (or the raw id) to a kid-facing surface.
    throw new Error(
      `buildJourneyView: rendered stone "${skillId}" has no KID_LABEL_MAP entry (D3) — extend KID_LABEL_MAP before this id can render.`,
    );
  }
  return label;
}

// ── §7/D2 — the ONE Status -> kid-audible/aria translation. Total over the
// Status union so no branch can silently fall through to a raw token.
// MA-85 (ADR-107): `not_yet` reuses the recorded "Earlier step" phrase (true
// for both of its cases: a below-band skill, or a step before one the child
// has already done) — no new spoken string. buildJourneyView also translates
// `not_yet` to `age_covered` (journeyStatusOf), so a stone never carries it.
// ──────────────────────────────────────────────────────────────────────
export const KID_STATUS_LABEL: Record<Status, string> = {
  mastered: "Finished",
  secure: "Strong",
  not_yet: "Earlier step",
  practicing: "Working on it",
  next: "Coming up",
  locked: "Not yet",
  age_covered: "Earlier step",
  suppressed: "Removed",
};

export function ariaLabelFor(kidLabel: string, status: Status): string {
  return `${kidLabel} — ${KID_STATUS_LABEL[status]}`;
}

// ── Input / output shapes ───────────────────────────────────────────

export interface JourneyChapterInput {
  id: string;
  titleKid: string;
  milestoneSkills: string[];
  supportSkills: string[];
}

export interface BuildJourneyViewInput {
  chapters: JourneyChapterInput[];
  statusById: Record<string, Status | undefined>;
  /** useTodaysMission().mission.chapterId — null when the engine chapter
   *  walk hasn't fired (fresh-learner / parent-assigned fallback). */
  missionChapterId: string | null;
  /** useTodaysMission().mission.recommendedSkill — the priors/age/
   *  attempt-derived focal skill. The header carries this verbatim
   *  (data-recommended-skill) regardless of whether it's rendered as a
   *  stone. It no longer decides pal placement in v2 (see palStoneId) —
   *  the pal stands on the fold's own frontier stone (D1/D4), which is
   *  frequently a DIFFERENT id than the priors-derived recommendation. */
  recommendedSkillId: string | null;
  /** MA-837: an EVIDENCE placement floor is active for this child
   *  (useTodaysMission().mission.placementFloorSkillIds). Lets the served-stone
   *  rule below also cover a `secure` focal still awaiting its cross-day confirm. */
  placedFloorSkillIds?: readonly string[];
  /** MA-849: Today's focal is an end-of-runway REVIEW ROTATION pick (useTodaysMission().mission.reviewRotation:
   *  every in-cap skill is confirmed and the mission reviews the oldest-seen one). That stone is tappable
   *  wherever the D4 window sits (the CTA opens it), with no relabel. */
  reviewRotationFocal?: boolean;
}

export type StoneKind = "milestone" | "pebble";

/** launch | narrate:<reason> | none — the STATUS-FIRST D4 outcome.
 *  `narrate:zero-pool` is IN-window but the skill has no servable pool
 *  (incl. the ms.read.02 frontier case) — data-tappable="true" (PR-B wires
 *  a narration response, never a silent dead tap). `narrate:beyond-ahead`/
 *  `narrate:beyond-behind` are OUT of window (by index OR the chapter cap)
 *  — data-tappable="false", but PR-B still wires a tap on these to a
 *  narration (a positive stay-on-Journey response, never a dead tap or a
 *  silent no-op). Direction: a stone whose OWN chapter sits strictly
 *  before the mission-current chapter is always "behind" (an earlier
 *  chapter than the one the kid is on); strictly after {current,
 *  current+1} is always "ahead" (a chapter not reached yet) — this is
 *  what keeps a fold-order-inverted stone's narration honest (e.g.
 *  ms.math.03 sorts to an EARLY lane index but is ch3 content the kid
 *  hasn't reached — "ahead", never "from earlier"). Within the capped
 *  chapters, an index-out-of-window stone is classified by its lane index
 *  relative to F. `launch` is a real navigation target in PR-B. `none` is
 *  an unreached defensive fallback (every rendered stone always resolves
 *  to one of the above) — PR-A wired NONE of these to a handler; PR-B
 *  wires launch + every narrate:* variant, never `none`. */
export type TapEligibility =
  | "launch"
  | "narrate:zero-pool"
  | "narrate:beyond-ahead"
  | "narrate:beyond-behind"
  | "none";

export interface JourneyStone {
  id: string;
  kind: StoneKind;
  kidLabel: string;
  status: Status;
  ariaLabel: string;
  tapEligibility: TapEligibility;
  /** tapEligibility !== "none" — the D4 hard rule as a render-ready bool. */
  tappable: boolean;
}

export interface JourneyChapterView {
  id: string;
  titleKid: string;
  /** True for exactly the WINDOW chapter: the mission walk's (or the
   *  fallback's) chapter, or, after MA-832's re-anchor (`anchorWindowOnFrontier`),
   *  the earlier chapter holding the frontier, so it can differ from the
   *  mission chapter — the `journey-current-chapter` testid anchor AND one of
   *  the two chapters D4's window is capped to (this one + the next). */
  isMissionCurrent: boolean;
  /** D1 widened rule — see module doc. Rendered on EVERY chapter now (the
   *  v1 "only before-current chapters get a banner" restriction is gone;
   *  full-path chapters all carry a header state). */
  banner: "celebration" | "neutral";
  readingStones: JourneyStone[];
  mathStones: JourneyStone[];
}

export interface JourneyView {
  /** ALL chapters, always — the v1 chapter-scoped bound (before/current/
   *  teaser/absent) is retired (ADR-100); PR-A is the full scrollable
   *  path. */
  chapters: JourneyChapterView[];
  /** Raw id, answer-safe, always present once a mission has resolved. */
  recommendedSkillId: string | null;
  /** The id of the stone the pal marker stands on (D1: "the frontier
   *  stone"), or null when no strand has any frontier left to stand on
   *  (every rendered stone in both lanes is mastered/secure/age_covered —
   *  the fully-covered case) — the caller falls back to a header-only pal
   *  in that case, same posture as v1's "recommendation isn't a stone". */
  palStoneId: string | null;
}

// ── Chapter completion / banner honesty (D1 widened rule) ────────────

const CHAIN_COMPLETE = new Set<Status>(["mastered", "secure", "age_covered"]);
const CONQUERED = new Set<Status>(["mastered", "secure"]);
const FRONTIER = new Set<Status>(["practicing", "next"]);

function chapterBanner(stones: JourneyStone[], notYetIds: ReadonlySet<string> = new Set()): "celebration" | "neutral" {
  if (stones.length === 0) return "neutral";
  // MA-85 (operator ruling S-5, 2026-10-05): a stone whose tree status is
  // `not_yet` renders walked-past ("Earlier step") but is NOT complete — the
  // child has not shown it — so it never lets a chapter celebrate.
  const allComplete = stones.every((s) => CHAIN_COMPLETE.has(s.status) && !notYetIds.has(s.id));
  const hasRealEvidence = stones.some((s) => CONQUERED.has(s.status));
  const hasFrontier = stones.some((s) => FRONTIER.has(s.status));
  return allComplete && hasRealEvidence && !hasFrontier ? "celebration" : "neutral";
}

// ── "current chapter" fallback — UNCHANGED from v1 (missionChapterId when
// resolvable, else the first chapter whose MILESTONES aren't all fold-
// complete, else the last chapter). This is a distinct concept from the
// D1 banner rule above (which now covers milestones+supports) — the
// mission-walk fallback keeps its own, narrower, milestone-only notion of
// "still has work to do" so it agrees with the engine's own chapter walk,
// which is milestone-gated (chapters.json's chapterGateStoneId lineage). ──
function isChapterMilestonesComplete(
  chapter: JourneyChapterInput,
  statusById: Record<string, Status | undefined>,
): boolean {
  return chapter.milestoneSkills
    .filter((id) => statusById[id] !== "suppressed")
    .every((id) => {
      const s = statusById[id];
      return s === "mastered" || s === "secure" || s === "age_covered";
    });
}

function currentChapterIndex(
  chapters: JourneyChapterInput[],
  statusById: Record<string, Status | undefined>,
  missionChapterId: string | null,
): number {
  if (missionChapterId != null) {
    const idx = chapters.findIndex((c) => c.id === missionChapterId);
    if (idx >= 0) return idx;
  }
  const firstIncomplete = chapters.findIndex((c) => !isChapterMilestonesComplete(c, statusById));
  if (firstIncomplete >= 0) return firstIncomplete;
  return chapters.length - 1;
}

/** MA-832 (spec docs/build/specs/MA-832-journey-current-chapter.md; reason
 *  recorded in the D4 spec). The chapter the D4 window is capped to.
 *  `routeIdx` is the mission-walk chapter, or the no-mission fallback. When
 *  the window it implies ({routeIdx, routeIdx+1}) holds NO startable frontier
 *  stone (a frontier stone inside its lane's index window) AND the child's
 *  frontier lies in EARLIER chapters, the window re-anchors to the earliest
 *  chapter holding a lane's lowest frontier stone. Otherwise `routeIdx`
 *  stands: a window that already holds a frontier is untouched, a frontier
 *  AHEAD of the route chapter is never pulled in (no chapter-jumping), and
 *  the window stays two chapters wide (it is not widened to every frontier). */
function anchorWindowOnFrontier(
  chapters: JourneyChapterInput[],
  routeIdx: number,
  lanes: ReadonlyArray<readonly [FlatStone[], number]>,
  servedNotYetId: string | null,
): number {
  if (windowHoldsStartableFrontier(chapters, routeIdx, lanes, servedNotYetId)) return routeIdx;
  const chapterIdx = new Map(chapters.map((c, i) => [c.id, i] as const));
  const behind: number[] = [];
  for (const [lane, F] of lanes) {
    if (F < lane.length) {
      const ci = chapterIdx.get(lane[F].chapterId);
      if (ci != null && ci < routeIdx) behind.push(ci);
    }
  }
  return behind.length > 0 ? Math.min(...behind) : routeIdx;
}

/** True when {idx, idx+1} holds a startable frontier stone: a frontier stone
 *  inside its lane's index window, or the served stone (counted on chapter
 *  alone: it starts its lesson wherever the index window sits, MA-85). */
function windowHoldsStartableFrontier(
  chapters: JourneyChapterInput[],
  idx: number,
  lanes: ReadonlyArray<readonly [FlatStone[], number]>,
  servedId: string | null,
): boolean {
  const chapterIdx = new Map(chapters.map((c, i) => [c.id, i] as const));
  const inWindowChapter = (i: number | undefined) => i === idx || i === idx + 1;
  for (const [lane, F] of lanes) {
    for (let i = 0; i < lane.length; i++) {
      if (!FRONTIER.has(lane[i].status)) continue;
      if ((lane[i].id === servedId || inIndexWindow(i, F, lane.length)) && inWindowChapter(chapterIdx.get(lane[i].chapterId))) return true;
    }
  }
  return false;
}

// ── D4 — zero-pool pre-check, memoized (pure content-pool lookup, static
// across a module lifetime — same content manifest every call). ────────
const zeroPoolCache = new Map<string, boolean>();

/** True when `skillId` has NO servable pool at all (`servedItemCountForSkill`
 *  returns 0 under the skill's own audience) — the D4 pre-check that turns
 *  what would otherwise be a `launch` into `narrate:zero-pool`, even for
 *  the frontier itself. (MA-671 slice 1b, 2026-09-19: ms.read.02 — the
 *  prior permanent example, audio-gated — is routed now and no longer
 *  zero-pool; see packRouting.ts's PACK_OVERRIDE.) Exported so the T-G
 *  zero-pool census bite can pin the LIVE set (today: empty, among
 *  rendered stones) without duplicating the predicate. */
export function isZeroPoolSkill(skillId: string): boolean {
  let cached = zeroPoolCache.get(skillId);
  if (cached === undefined) {
    cached = servedItemCountForSkill(skillId, 8, audienceForSkill(skillId)) <= 0;
    zeroPoolCache.set(skillId, cached);
  }
  return cached;
}

// ── D4 — the global (cross-chapter), fold-sorted per-strand lane the
// window math runs over. ──────────────────────────────────────────────

interface FlatStone {
  id: string;
  chapterId: string;
  kind: StoneKind;
  status: Status;
}

function strandBucket(id: string): "reading" | "math" {
  // Mirrors v1's own lane fold: reading milestones/supports render in the
  // reading lane; everything else (math, and any hypothetical future
  // writing skill — none exist in the manifest today) folds into math, so
  // a stone's lane can never disagree with its manifest strand by omission.
  return BY_ID[id]?.strand === "reading" ? "reading" : "math";
}

function buildFlatLanes(
  chapters: JourneyChapterInput[],
  statusById: Record<string, Status | undefined>,
): { reading: FlatStone[]; math: FlatStone[] } {
  const reading: FlatStone[] = [];
  const math: FlatStone[] = [];
  for (const chapter of chapters) {
    const entries: Array<{ id: string; kind: StoneKind }> = [
      ...chapter.milestoneSkills.map((id) => ({ id, kind: "milestone" as const })),
      ...chapter.supportSkills.map((id) => ({ id, kind: "pebble" as const })),
    ];
    for (const { id, kind } of entries) {
      const status = statusById[id];
      if (status === "suppressed") continue; // D2: suppressed never renders.
      const flat: FlatStone = { id, chapterId: chapter.id, kind, status: status ?? "locked" };
      (strandBucket(id) === "reading" ? reading : math).push(flat);
    }
  }
  reading.sort((a, b) => byStrandOrder(a.id, b.id));
  math.sort((a, b) => byStrandOrder(a.id, b.id));
  return { reading, math };
}

/** F = the lowest lane index whose status is a frontier status. If none
 *  exists (all-covered/all-mastered lane), F = lane.length (the sentinel
 *  the window function below reads as "last 3, replay-only").
 *  MA-85 (operator rulings S-5 / S-R2-1): a served focal `not_yet` skill
 *  already reads `practicing` here (buildJourneyView), so it is a frontier
 *  stone by status; no separate anchor is needed. */
function frontierIndex(lane: FlatStone[]): number {
  const idx = lane.findIndex((s) => FRONTIER.has(s.status));
  return idx === -1 ? lane.length : idx;
}

/** D4 window membership by INDEX only (the chapter cap is applied
 *  separately by the caller, against the stone's OWN chapter). */
function inIndexWindow(index: number, F: number, laneLength: number): boolean {
  if (F === laneLength) {
    // No frontier anywhere in this lane: last 3 stones, replay-only.
    return index >= Math.max(0, laneLength - 3) && index <= laneLength - 1;
  }
  return index >= F - 3 && index <= F + 3;
}

/** Direction for an out-of-window stone's narration (D4). Chapter order
 *  wins over raw lane index — see the TapEligibility doc comment above for
 *  why (the fold-order-inversion class). Only called when the stone is
 *  already known to be out of window. */
function narrateReasonFor(
  stone: FlatStone,
  index: number,
  F: number,
  laneLength: number,
  chapterIndexById: Readonly<Record<string, number>>,
  currentIdx: number,
): "narrate:beyond-ahead" | "narrate:beyond-behind" {
  const chapterIdx = chapterIndexById[stone.chapterId] ?? currentIdx;
  if (chapterIdx < currentIdx) return "narrate:beyond-behind";
  if (chapterIdx > currentIdx + 1) return "narrate:beyond-ahead";
  // Same capped chapter(s) as the window itself — the exclusion is purely
  // an index one. F === laneLength is the all-covered/replay-only lane
  // sentinel: no frontier exists anywhere, so there is no "ahead" to be —
  // everything outside the last-3 window is simply further behind.
  if (F === laneLength) return "narrate:beyond-behind";
  return index < F ? "narrate:beyond-behind" : "narrate:beyond-ahead";
}

function tapEligibilityFor(
  stone: FlatStone,
  index: number,
  F: number,
  laneLength: number,
  chapterCap: ReadonlySet<string>,
  chapterIndexById: Readonly<Record<string, number>>,
  currentIdx: number,
  servedNotYetId: string | null = null,
): TapEligibility {
  // MA-85 (operator ruling MUST-R2-1): the served not_yet stone — the pal's
  // stone — always starts its lesson, wherever the D4 window sits (the window
  // itself is unchanged, so earlier "next" stones stay launchable too).
  const inWindow =
    stone.id === servedNotYetId || (inIndexWindow(index, F, laneLength) && chapterCap.has(stone.chapterId));
  if (inWindow) {
    if (isZeroPoolSkill(stone.id)) return "narrate:zero-pool";
    return "launch";
  }
  return narrateReasonFor(stone, index, F, laneLength, chapterIndexById, currentIdx);
}

/** D1: the pal stands on "the frontier stone (see D4 for which)" —
 *  concretely: the lowest-index frontier stone of the RECOMMENDED skill's
 *  own strand, when that strand actually has a frontier; otherwise the
 *  first frontier found (reading checked before math, an arbitrary but
 *  deterministic tie-break — no fixture pins the reading-vs-math order
 *  when neither is "the recommended strand", only that SOME real frontier
 *  is picked over none when one exists). Null when neither lane has any
 *  frontier left (the fully-covered case) — the caller falls back to a
 *  header-only pal render, mirroring v1's off-stone posture. */
function pickPalStoneId(
  readingLane: FlatStone[],
  mathLane: FlatStone[],
  recommendedSkillId: string | null,
  servedNotYetId: string | null = null,
  isLaunchable: (id: string) => boolean = () => true,
): string | null {
  // MA-85 (operator ruling S-5): a served focal skill whose tree status is
  // `not_yet` keeps the pal — the not_yet -> walked-past translation must not
  // move the pal off the skill the mission actually serves.
  if (servedNotYetId != null) return servedNotYetId;
  // MA-832: the pal stands on the lowest frontier stone a tap can launch
  // (in the D4 window), never on one a tap cannot pick.
  const lowestLaunchableFrontier = (lane: FlatStone[]): string | null =>
    lane.find((s) => FRONTIER.has(s.status) && isLaunchable(s.id))?.id ?? null;
  const readingFrontier = lowestLaunchableFrontier(readingLane);
  const mathFrontier = lowestLaunchableFrontier(mathLane);
  const recommendedStrand = recommendedSkillId != null ? strandBucket(recommendedSkillId) : null;
  if (recommendedStrand === "reading" && readingFrontier != null) return readingFrontier;
  if (recommendedStrand === "math" && mathFrontier != null) return mathFrontier;
  return readingFrontier ?? mathFrontier;
}

// ── Build ──────────────────────────────────────────────────────────

/** MA-1029 (spec docs/build/specs/MA-1029-math-floor-frontier.md). The fold's MA-91
 *  band ceiling keeps every above-band skill `locked` while on-band work remains in
 *  the strand. When the only unresolved on-band work is a skill no chapter lists
 *  (measured: ms.math.00e "Trace numerals 0-5" for a 4.5-year-old, the fold's math
 *  "Now"), nothing on the Journey or Today can ever resolve it, so a lane the child
 *  has entered keeps future stones and no frontier for the rest of the run. For the
 *  Journey that on-band work is exhausted, so the lane's stones get the lift MA-91
 *  gives an exhausted strand: the first stone in lane order that is `locked` and has
 *  every prerequisite resolved becomes the lane's single "next". Applied per lane only
 *  when the lane has no frontier stone, holds at least one resolved stone, and the
 *  fold's own frontier for that strand sits only on skills that are not stones AND
 *  that no stone depends on (no rendered stone, in any lane, lists one as a
 *  prerequisite, directly or transitively; operator decision D-1020-2). A walkback
 *  prerequisite (ms.math.00a/00c/00f, ms.read.05: served before the stone that needs
 *  it) is resolvable by the child, so it never triggers the lift. The parent tree and
 *  the mission are unchanged (the fold is not touched). */
function liftOffJourneyCeiling(
  chapters: JourneyChapterInput[],
  statusById: Record<string, Status | undefined>,
  foldStatusById: Readonly<Record<string, Status | undefined>>,
): void {
  const rendered = new Set(chapters.flatMap((c) => [...c.milestoneSkills, ...c.supportSkills]));
  // Every skill some rendered stone depends on (transitive prerequisites, all lanes).
  const dependedOn = new Set<string>();
  const stack = [...rendered].flatMap((id) => BY_ID[id]?.prerequisites ?? []);
  while (stack.length > 0) {
    const p = stack.pop() as string;
    if (dependedOn.has(p)) continue;
    dependedOn.add(p);
    stack.push(...(BY_ID[p]?.prerequisites ?? []));
  }
  // A parent-removed (`suppressed`) prerequisite counts as resolved: the fold's own
  // CHAIN_ALIVE posture (skillTreeStatus.ts, MA-89), so a removal advances the chain
  // instead of leaving a hole the lane could never get past.
  const RESOLVED = new Set<Status>(["mastered", "secure", "age_covered", "suppressed"]);
  for (const lane of ["reading", "math"] as const) {
    const stones = [...rendered]
      .filter((id) => strandBucket(id) === lane && statusById[id] !== "suppressed")
      .sort(byStrandOrder);
    const st = (id: string): Status => statusById[id] ?? "locked";
    if (stones.some((id) => FRONTIER.has(st(id)))) continue;
    if (!stones.some((id) => CHAIN_COMPLETE.has(st(id)))) continue;
    const offJourneyFrontier = Object.entries(foldStatusById)
      .filter(([id, s]) => !rendered.has(id) && BY_ID[id] != null && strandBucket(id) === lane && s != null && FRONTIER.has(s))
      .map(([id]) => id);
    // Held for good only by dead-end skills: at least one, and none of them is a prerequisite of any stone.
    if (offJourneyFrontier.length === 0 || offJourneyFrontier.some((id) => dependedOn.has(id))) continue;
    const lifted = stones.find(
      (id) => st(id) === "locked" && (BY_ID[id]?.prerequisites ?? []).every((p) => RESOLVED.has(statusById[p] ?? "locked")),
    );
    if (lifted != null) statusById[lifted] = "next";
  }
}

/** MA-85 (ADR-107) — the kid surface keeps the pre-MA-85 vocabulary: a
 *  `not_yet` skill (tried below band, or an on/above-band prerequisite of a
 *  secured skill) is a walked-past "Earlier step" stone like `age_covered`,
 *  so JourneyPath's visuals, the milestone-only current-chapter fallback and
 *  the MA-495 lane hook treat it as entered, never as future. Exceptions
 *  (operator rulings S-5 / MUST-R2-1 / S-R2-1 / S-R2-2, applied in
 *  buildJourneyView): a not_yet stone never completes a chapter's celebration
 *  banner; the served focal skill, when not_yet, keeps the pal, reads and
 *  speaks "Working on it", always starts its lesson on tap, and with no
 *  mission chapter its own chapter is current. The parent tree carries the
 *  distinction. */
export function journeyStatusOf(status: Status | undefined): Status | undefined {
  return status === "not_yet" ? "age_covered" : status;
}

export function buildJourneyView(input: BuildJourneyViewInput): JourneyView {
  const { chapters, missionChapterId, recommendedSkillId, placedFloorSkillIds, reviewRotationFocal } = input;
  const statusById: Record<string, Status | undefined> = {};
  for (const [id, st] of Object.entries(input.statusById)) statusById[id] = journeyStatusOf(st);
  // MA-85 (operator ruling S-5): the ids the tree calls `not_yet`. They render
  // walked-past, but never complete a chapter's celebration banner, and the
  // served focal skill among them keeps the pal (and the D4 window).
  const notYetIds = new Set(Object.entries(input.statusById).filter(([, st]) => st === "not_yet").map(([id]) => id));
  // The served focal skill when the tree calls it not_yet and it renders as a
  // stone (a chapter lists it).
  const servedChapterIdx =
    recommendedSkillId != null && notYetIds.has(recommendedSkillId)
      ? chapters.findIndex((c) => c.milestoneSkills.includes(recommendedSkillId) || c.supportSkills.includes(recommendedSkillId))
      : -1;
  const servedNotYetId = servedChapterIdx >= 0 ? recommendedSkillId : null;
  // MA-85 (operator ruling S-R2-1): that stone reads and speaks the recorded
  // "Working on it" (`practicing`), so the stone, the pal's bubble and the
  // voice agree; it is a frontier stone exactly where the v2 window anchor
  // already put it.
  if (servedNotYetId != null) statusById[servedNotYetId] = "practicing";
  // MA-1029: a lane whose ceiling is held by a skill the Journey never renders gets its next stone.
  liftOffJourneyCeiling(chapters, statusById, input.statusById);
  // MA-85 (operator ruling S-R2-2): with no resolvable mission chapter (the
  // parent-assigned fallback), the served not_yet skill's chapter is current.
  const missionResolvable = missionChapterId != null && chapters.some((c) => c.id === missionChapterId);
  const layout = (servedId: string | null) => {
    const { reading: readingLane, math: mathLane } = buildFlatLanes(chapters, statusById);
    const readingF = frontierIndex(readingLane);
    const mathF = frontierIndex(mathLane);
    const routeIdx =
      !missionResolvable && servedChapterIdx >= 0
        ? servedChapterIdx
        : currentChapterIndex(chapters, statusById, missionChapterId);
    const lanes = [
      [readingLane, readingF],
      [mathLane, mathF],
    ] as const;
    // MA-832: the window must hold the child's frontier. The mission chapter
    // (engine walk) and the no-mission fallback (first chapter with unfinished
    // milestones) both ignore a chapter whose milestones are age-covered while
    // its support stones are still frontier, so for a fresh 5-year-old they
    // resolve to ch2 with every frontier stone in ch1. Re-anchor only then.
    const currentIdx = anchorWindowOnFrontier(chapters, routeIdx, lanes, servedId);
    return { readingLane, mathLane, readingF, mathF, routeIdx, lanes, currentIdx };
  };
  let servedId: string | null = servedNotYetId;
  let L = layout(servedId);
  // MA-834 (decision in the D4 spec): a PLACED child's engine mission can sit in
  // a chapter BEFORE the fold frontier (a placement floor exempts the skill from
  // the age masks, so the walk serves e.g. ms.read.01 while the fold's frontier
  // is in ch3) and the fold calls that skill `age_covered`, so no stone in the
  // window is a frontier. The Journey then shows what Today serves: the mission's
  // focal skill, when it is a stone of the MISSION chapter and the window still
  // holds no startable frontier, is the served stone (the MA-85 mechanism:
  // reads "Working on it", starts its lesson on tap, holds the pal). The window
  // is neither widened nor pulled forward to the fold frontier.
  if (
    servedId == null &&
    missionResolvable &&
    recommendedSkillId != null &&
    // MA-837: also `secure` when the focal IS an evidence floor's own skill (the placed skill a session
    // or two later, still served until its cross-day confirm (MA-297) completes the chapter; without
    // it the Journey holds nothing to pick on those days). Any other secure focal, placed child or not,
    // is not relabelled (an end-of-curriculum review skill stays secure). MA-849: a REVIEW ROTATION focal
    // is by construction confirmed (it holds chapter credit under the confirm gate), so it is never
    // relabelled "Working on it" even when it is an evidence floor's own skill (33 such 7.5 rows).
    (statusById[recommendedSkillId] === "age_covered" || (placedFloorSkillIds?.includes(recommendedSkillId) === true && statusById[recommendedSkillId] === "secure" && reviewRotationFocal !== true)) &&
    chapters[L.routeIdx] != null &&
    (chapters[L.routeIdx].milestoneSkills.includes(recommendedSkillId) || chapters[L.routeIdx].supportSkills.includes(recommendedSkillId)) &&
    !windowHoldsStartableFrontier(chapters, L.currentIdx, L.lanes, null)
  ) {
    servedId = recommendedSkillId;
    statusById[servedId] = "practicing";
    L = layout(servedId);
  }
  const { readingLane, mathLane, readingF, mathF, currentIdx } = L;
  const chapterCap = new Set<string>(
    [chapters[currentIdx]?.id, chapters[currentIdx + 1]?.id].filter((id): id is string => id != null),
  );
  const chapterIndexById: Record<string, number> = Object.fromEntries(chapters.map((c, i) => [c.id, i]));

  // Precompute every rendered stone's tapEligibility ONCE (index is
  // positional within its own lane's sort — the same order a chapter's
  // per-lane stone list below is a stable filtered SUBSET of).
  const eligibilityById = new Map<string, TapEligibility>();
  readingLane.forEach((s, i) =>
    eligibilityById.set(s.id, tapEligibilityFor(s, i, readingF, readingLane.length, chapterCap, chapterIndexById, currentIdx, servedId)),
  );
  mathLane.forEach((s, i) =>
    eligibilityById.set(s.id, tapEligibilityFor(s, i, mathF, mathLane.length, chapterCap, chapterIndexById, currentIdx, servedId)),
  );

  // MA-851: a placed child's Today focal that IS an evidence floor's own skill is the
  // served stone wherever the D4 window sits (the MA-85 mechanism, applied to the placed
  // skill): it starts its lesson on tap and, while not yet played, reads "Working on it". The
  // mission names the floor skill when its walk would pick a foundation skill below the
  // floor (progression.buildTodaysMission), and a floor several chapters past the window
  // is `narrate:beyond-ahead` by index, so without this the CTA opens a skill whose
  // Journey stone refuses the tap and is not a frontier stone (measured: 21-28
  // untappable-Today days on the 4.5- and 5-year-olds with a reading floor above
  // reading.06; no pickable stone on the day a 4-year-old first plays the floor).
  // Only a `locked` or `secure` focal that is AHEAD of the window is lifted (a floor behind
  // the window is the end-of-curriculum replay, MA-839, and a finished replay is never
  // relabelled); the window itself is unchanged and a zero-pool stone is left narrating.
  const statusOverride = new Map<string, Status>();
  if (missionResolvable && recommendedSkillId != null && placedFloorSkillIds?.includes(recommendedSkillId) === true) {
    const e = eligibilityById.get(recommendedSkillId);
    const st = statusById[recommendedSkillId];
    if (e === "narrate:beyond-ahead" && (st === "locked" || st === "secure") && !isZeroPoolSkill(recommendedSkillId)) {
      // `locked` (the placed skill not yet played) reads "Working on it"; a `secure` focal may be a
      // finished replay (fluent and confirmed: MA-839's pin, never relabelled), so it only becomes tappable.
      if (st === "locked") statusOverride.set(recommendedSkillId, "practicing");
      eligibilityById.set(recommendedSkillId, "launch");
    }
  }

  // MA-851: for a child who has an evidence floor, Today's focal that is a `secure` stone BEHIND the
  // window (its cross-day confirm (MA-297) still pending, so the mission serves it again while the
  // window has moved on: measured, ms.math.00g for a 4.5-year-old, 2 days) is tappable: the CTA opens
  // it, so the stone must start it. Only the tap changes (no relabel); a zero-pool stone keeps narrating.
  if (missionResolvable && recommendedSkillId != null && placedFloorSkillIds?.some((id) => id.startsWith("ms.read.")) === true && !isZeroPoolSkill(recommendedSkillId)) {
    if (
      eligibilityById.get(recommendedSkillId) === "narrate:beyond-behind" &&
      statusById[recommendedSkillId] === "secure" &&
      // the window is still in the FIRST chapter and the floor skill's stone is in a later one: a
      // child just starting out, working up to a proven floor, whose confirm-pending stone the index
      // rule leaves behind the window. Anything else (a floor inside or behind the window, or a window
      // past chapter 1: the older children's later-run replays, MA-839) keeps narrating; the same lift
      // would also serve the 5.5-year-olds with a reading floor (4 rows, follow-up, see the spec).
      currentIdx === 0 &&
      placedFloorSkillIds.some(
        (id) =>
          id.startsWith("ms.read.") &&
          chapterIndexById[chapters.find((c) => c.milestoneSkills.includes(id) || c.supportSkills.includes(id))?.id ?? ""] > currentIdx,
      ) &&
      !placedFloorSkillIds.includes(recommendedSkillId)
    ) {
      eligibilityById.set(recommendedSkillId, "launch");
    }
  }

  // MA-849: the end-of-runway review rotation names a confirmed (`secure`) skill from anywhere in the
  // curriculum, mostly BEHIND the window (the window sits on the newest chapter), so the index rule
  // would narrate a stone the CTA opens (measured: 170 of 419 terminal 7.5 days at one session a day,
  // 2733 of 5287 at two). The CTA opens it, so the stone starts it: only the tap changes (no relabel: a
  // finished review skill is never "Working on it", MA-839); a zero-pool stone keeps narrating.
  if (missionResolvable && reviewRotationFocal === true && recommendedSkillId != null && !isZeroPoolSkill(recommendedSkillId)) {
    const e = eligibilityById.get(recommendedSkillId);
    if ((e === "narrate:beyond-behind" || e === "narrate:beyond-ahead") && statusById[recommendedSkillId] === "secure") {
      eligibilityById.set(recommendedSkillId, "launch");
    }
  }

  // MA-851: the reading frontier skips what the /play/foundation guard would redirect. With an
  // evidence reading floor whose own stone has not been played, a not-yet-started foundation stone
  // strictly below the floor (the guard sends its tap to the floor skill, and the Journey tap
  // narrates it) is not a frontier: it reads as the future stone it is for this child, and the
  // floor skill's stone is the frontier ("Up next", tappable). Without it the only frontier stone
  // of a placed 4-year-old is the one stone nothing can open, for the days between the
  // age-start skills' cross-day confirms and the first play of the floor skill.
  const readingFloorId = placedFloorSkillIds?.find((id) => id.startsWith("ms.read.")) ?? null;
  if (readingFloorId != null && statusById[readingFloorId] === "locked" && !isZeroPoolSkill(readingFloorId)) {
    const floorLike = { strand: "reading" as const, legacyId: "", skillId: readingFloorId, ordinal: skillCurriculumOrdinal("reading", readingFloorId) };
    const blocked = readingLane.filter((s) => statusById[s.id] === "next" && foundationRedirectSkillId(s.id, floorLike) != null);
    // ... and only on a day when nothing else on the Journey is a startable frontier stone (a child with
    // another startable frontier stone, e.g. a math one, keeps the view they had).
    const anyStartable = [...readingLane, ...mathLane].some(
      (s) => (statusById[s.id] === "next" || statusById[s.id] === "practicing") && eligibilityById.get(s.id) === "launch" && !blocked.includes(s),
    );
    if (blocked.length > 0 && !anyStartable && eligibilityById.has(readingFloorId)) {
      for (const b of blocked) statusOverride.set(b.id, "locked");
      statusOverride.set(readingFloorId, "next");
      eligibilityById.set(readingFloorId, "launch");
    }
  }

  const toStone = (flat: FlatStone): JourneyStone => {
    const kidLabel = kidLabelFor(flat.id);
    const tapEligibility = eligibilityById.get(flat.id) ?? "none";
    const status: Status = statusOverride.get(flat.id) ?? flat.status;
    return {
      id: flat.id,
      kind: flat.kind,
      kidLabel,
      status,
      ariaLabel: ariaLabelFor(kidLabel, status),
      tapEligibility,
      // D4: in-window outcomes (launch + the zero-pool narration) carry a
      // real tap response; out-of-window narrate:beyond-* stones keep
      // data-tappable="false" (still focusable/clickable in the DOM for
      // their own narration, but never claim window membership).
      tappable: tapEligibility === "launch" || tapEligibility === "narrate:zero-pool",
    };
  };

  const chapterViews: JourneyChapterView[] = chapters.map((chapter, i) => {
    const readingStones = readingLane.filter((s) => s.chapterId === chapter.id).map(toStone);
    const mathStones = mathLane.filter((s) => s.chapterId === chapter.id).map(toStone);
    return {
      id: chapter.id,
      titleKid: chapter.titleKid,
      isMissionCurrent: i === currentIdx,
      banner: chapterBanner([...readingStones, ...mathStones], notYetIds),
      readingStones,
      mathStones,
    };
  });

  return {
    chapters: chapterViews,
    recommendedSkillId: recommendedSkillId ?? null,
    palStoneId: pickPalStoneId(readingLane, mathLane, recommendedSkillId ?? null, servedId, (id) => eligibilityById.get(id) === "launch"),
  };
}
